// src/pages/Training/TrainingPage.jsx
import { useCallback, useMemo, useRef, useState } from "react";
import { TrainingAI } from "../../lib/training";
import { buildReportHtml } from "../../lib/reportRenderer"; // ✅ 추가: 마크업 변환 함수

function CopyButton({ text, className = "copy", label = "복사" }) {
  const onCopy = useCallback(async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      alert("클립보드에 복사되었습니다.");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      alert("클립보드에 복사되었습니다.");
    }
  }, [text]);

  return (
    <button type="button" onClick={onCopy} className={className}>
      {label}
    </button>
  );
}

// ✅ 추가: 오류문구 안전 출력용(TrainingFollowup과 동일 계열)
function escapeHtml(s = "") {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

export default function TrainingPage() {
  // ===== 1) AI 상담 예행연습 (거절유형 스크립트) =====
  const [objection, setObjection] = useState("price");
  const [scriptDraft, setScriptDraft] = useState("");
  const [scriptLoading, setScriptLoading] = useState(false);
  const [scriptError, setScriptError] = useState("");

  // ===== 2) 코칭 분석 =====
  const [coachingInput, setCoachingInput] = useState("");
  const [coachingDraft, setCoachingDraft] = useState("");
  const [coachingLoading, setCoachingLoading] = useState(false);
  const [coachingError, setCoachingError] = useState("");

  // ===== 3) 환자 시뮬레이터 =====
  const [persona, setPersona] = useState("price-sensitive");
  const [chat, setChat] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [simBusy, setSimBusy] = useState(false);
  const [report, setReport] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const [reportError, setReportError] = useState("");

  const busy = useMemo(
    () => scriptLoading || coachingLoading || simBusy || reportBusy,
    [scriptLoading, coachingLoading, simBusy, reportBusy]
  );

  // ✅ 추가: 공통 마크업 렌더 유틸 (초안 문자열 -> HTML)
  const renderDraftHtml = useCallback((draft) => {
    return buildReportHtml(draft || "", { iconHTML: "" });
  }, []);

  // ===== Handlers =====
  const onGenerateScript = useCallback(async () => {
    setScriptLoading(true);
    setScriptError("");
    setScriptDraft("");
    try {
      const res = await TrainingAI.generateScript({ objectionKey: objection });
      setScriptDraft((res?.answer || "").trim());
    } catch (e) {
      setScriptError(e?.message || "생성 실패");
    } finally {
      setScriptLoading(false);
    }
  }, [objection]);

  const onAnalyze = useCallback(async () => {
    if (!coachingInput.trim()) {
      alert("분석할 상담 스크립트를 입력해주세요.");
      return;
    }
    setCoachingLoading(true);
    setCoachingError("");
    setCoachingDraft("");
    try {
      const res = await TrainingAI.coaching({ script: coachingInput.trim() });
      setCoachingDraft((res?.answer || "").trim());
    } catch (e) {
      setCoachingError(e?.message || "분석 실패");
    } finally {
      setCoachingLoading(false);
    }
  }, [coachingInput]);

  const historyRef = useRef([]);

  const startSimulation = useCallback(async () => {
    setSimBusy(true);
    setReport("");
    setReportError("");
    setChat([]);
    historyRef.current = [];
    try {
      const res = await TrainingAI.simFirst({ persona });
      const first = (res?.answer || "").trim();
      if (first) {
        setChat([{ role: "patient", text: first }]);
        historyRef.current.push({ role: "patient", text: first });
      }
    } catch (e) {
      alert(e?.message || "시뮬레이션 시작 실패");
    } finally {
      setSimBusy(false);
    }
  }, [persona]);

  const sendChat = useCallback(async () => {
    const t = chatInput.trim();
    if (!t || simBusy) return;
    setChatInput("");
    const next = [...chat, { role: "user", text: t }];
    setChat(next);
    historyRef.current.push({ role: "user", text: t });

    setSimBusy(true);
    try {
      const res = await TrainingAI.simChat({
        persona,
        history: historyRef.current,
      });
      const reply = (res?.answer || "").trim();
      if (reply) {
        setChat((prev) => [...prev, { role: "patient", text: reply }]);
        historyRef.current.push({ role: "patient", text: reply });
      }
    } catch (e) {
      alert(e?.message || "응답 생성 실패");
    } finally {
      setSimBusy(false);
    }
  }, [chatInput, chat, persona, simBusy]);

  const onEnter = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      sendChat();
    }
  };

  const endSimulation = useCallback(async () => {
    setReportBusy(true);
    setReportError("");
    setReport("");
    try {
      const res = await TrainingAI.simReport({
        persona,
        history: historyRef.current,
      });
      setReport((res?.answer || "").trim());
    } catch (e) {
      setReportError(e?.message || "리포트 생성 실패");
    } finally {
      setReportBusy(false);
    }
  }, [persona]);

  // ===== Render =====
  return (
    <section className="training">
      <div id="content_title">
        <h2>AI MaC 상담(트레이닝)</h2>
        <p>상담팀 역량 강화를 위한 AI 기반 훈련 도구</p>
      </div>

      <div className="content">
        {/* 1) 상담 예행연습 */}
        <div className="section borderType">
          <h3 className="sectionTitle">AI와 함께하는 상담 예행연습</h3>

          <div>
            <label className="inputTitle">환자 거절 유형 선택</label>
            <select className=" useform" value={objection} onChange={(e) => setObjection(e.target.value)}>
              <option value="price">"가격이 너무 비싸요." (가격-가치 불일치)</option>
              <option value="decision">"가족과 상의해봐야 해요." (결정의 부담)</option>
              <option value="trust">"효과가 있을지 모르겠어요." (신뢰 부족)</option>
              <option value="process">"일단 생각해보고 연락드릴게요." (프로세스 마찰)</option>
            </select>
          </div>

          <div>
            <button className="submitbtn" onClick={onGenerateScript} disabled={scriptLoading || busy}>
              {scriptLoading ? "생성중.." : "AI 맞춤 스크립트 생성"}
            </button>
          </div>

          {(scriptDraft || scriptError) && (
            <div className="trf-box">
              <div className="trf-box-head">AI 생성 스크립트</div>
              <div className="trf-box-body">
                {/* ✅ 변경: 마크업 적용 */}
                {scriptError ? (
                  <div
                    className="trf-markdown"
                    dangerouslySetInnerHTML={{
                      __html: `<p class="report-paragraph">오류: ${escapeHtml(scriptError)}</p>`,
                    }}
                  />
                ) : (
                  <div
                    className="trf-markdown"
                    dangerouslySetInnerHTML={{
                      __html: renderDraftHtml(scriptDraft),
                    }}
                  />
                )}

                <div>
                  <CopyButton text={scriptDraft} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 2) 코칭 분석 */}
        <div className="section borderType">
          <h3 className="sectionTitle">실전! AI 상담 코칭</h3>

          <div>
            <label className="inputTitle">분석할 상담 스크립트 입력</label>
            <textarea
              rows={8}
              className=" useform"
              placeholder="상담팀장과 환자의 대화 내용을 붙여넣으세요."
              value={coachingInput}
              onChange={(e) => setCoachingInput(e.target.value)}
            />
          </div>

          <div>
            <button className="submitbtn" onClick={onAnalyze} disabled={coachingLoading || busy}>
              {coachingLoading ? "분석중.." : "내 스크립트 분석 요청"}
            </button>
          </div>

          {(coachingDraft || coachingError) && (
            <div className="trf-box ">
              <div className="trf-box-head">AI 코칭 피드백</div>
              <div className="trf-box-body">
                {/* ✅ 변경: 마크업 적용 */}
                {coachingError ? (
                  <div
                    className="trf-markdown"
                    dangerouslySetInnerHTML={{
                      __html: `<p class="report-paragraph">오류: ${escapeHtml(coachingError)}</p>`,
                    }}
                  />
                ) : (
                  <div
                    className="trf-markdown"
                    dangerouslySetInnerHTML={{
                      __html: renderDraftHtml(coachingDraft),
                    }}
                  />
                )}

                <div>
                  <CopyButton text={coachingDraft} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 3) 환자 시뮬레이터 */}
        <div className="section borderType">
          <h3 className="sectionTitle">실전! AI 환자 시뮬레이터</h3>

          <div>
            <label className="inputTitle">상담할 환자 페르소나 선택</label>
            <select className=" useform" value={persona} onChange={(e) => setPersona(e.target.value)}>
              <option value="price-sensitive">가격 민감형 대학생</option>
              <option value="skeptical-worker">효과 의심형 직장인</option>
              <option value="indecisive-homemaker">결정 유보형 주부</option>
            </select>
          </div>

          <div>
            <button className="submitbtn" onClick={startSimulation} disabled={simBusy || busy}>
              {simBusy ? "시작중.." : "시뮬레이션 시작"}
            </button>
          </div>

          {/* 채팅 UI */}
          {chat.length > 0 && (
            <div className="trf-box">
              <div className="trf-box-head">시뮬레이션 대화</div>
              <div className="trf-box-body">
                <div className="chatbox">
                  <div className="chatlog">
                    {chat.map((m, i) => (
                      <div key={i} className={`bubble ${m.role === "user" ? "from-user" : "from-patient"}`}>
                        <div className="sim-text">{m.text}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="chatInput">
                  <input value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyDown={onEnter} placeholder="환자에게 보낼 멘트를 입력하세요" />
                  <button onClick={sendChat} disabled={simBusy || busy}>
                    전송
                  </button>
                </div>

                <div className="ahctActions">
                  <button onClick={endSimulation} disabled={reportBusy || busy}>
                    {reportBusy ? "리포트 생성중.." : "시뮬레이션 종료 및 리포트"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {(report || reportError) && (
            <div className="trf-box ">
              <div className="trf-box-head">AI 시뮬레이션 리포트</div>
              <div className="trf-box-body">
                <pre>{reportError ? `오류: ${reportError}` : report}</pre>
                <div>
                  <CopyButton text={report} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
